import React, { useEffect, useMemo, useState } from "react";
import { Global } from "../helpers/Global";

export const CrearReporteSIL = ({ onCreated }) => {
  const [mensaje, setMensaje] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Nodo asignado (info para el usuario)
  const [miNodo, setMiNodo] = useState(null);
  const [loadingNodo, setLoadingNodo] = useState(true);

  const [form, setForm] = useState({
    estado: "verde",
    comentario: "",
  });

  const authHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem("token")}`,
  });

  // 🔎 Ajusta esto a cómo guardas tú el usuario en tu app
  const getMyUserId = () => {
    // Caso 1: guardas "user" como JSON
    try {
      const raw = localStorage.getItem("user");
      if (raw) {
        const u = JSON.parse(raw);
        if (u?.id) return Number(u.id);
      }
    } catch (e) {
      // ignore
    }

    // Caso 2: guardas "user_id" suelto
    const rawId = localStorage.getItem("user_id");
    if (rawId && Number.isFinite(Number(rawId))) return Number(rawId);

    return null;
  };

  // Cargar mi nodo asignado usando organigrama (hasta tener endpoint dedicado)
  useEffect(() => {
    (async () => {
      setLoadingNodo(true);
      setMensaje("");

      try {
        const myUserId = getMyUserId();

        // Si no podemos saber quién soy, no podemos resolver el nodo en frontend
        if (!myUserId) {
          setMiNodo(null);
          setMensaje("❌ No se pudo identificar el usuario actual (user_id).");
          return;
        }

        const res = await fetch(`${Global.url}nodos/organigrama`, {
          headers: authHeaders(),
        });

        const data = await res.json().catch(() => []);
        if (!res.ok) {
          setMiNodo(null);
          setMensaje("❌ No se pudo cargar el nodo asignado.");
          return;
        }

        const lista = Array.isArray(data) ? data : [];
        const nodo = lista.find((n) => Number(n.usuario_id) === Number(myUserId)) || null;

        setMiNodo(nodo);

        if (!nodo) {
          setMensaje("⚠️ No tienes un nodo asignado. Contacta con un administrador.");
        }
      } catch (e) {
        console.error(e);
        setMiNodo(null);
        setMensaje("❌ Error de red obteniendo el nodo asignado.");
      } finally {
        setLoadingNodo(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const estadoNoVerde = useMemo(() => {
    const v = (form.estado || "").toLowerCase();
    return v !== "verde";
  }, [form.estado]);

  const puedeEnviar = useMemo(() => {
    if (!miNodo) return false; // sin nodo asignado, no se puede reportar
    if (!form.estado?.trim()) return false;
    if (estadoNoVerde && !(form.comentario || "").trim()) return false;
    return true;
  }, [miNodo, form.estado, form.comentario, estadoNoVerde]);

  const resetForm = () => {
    setForm({
      estado: "verde",
      comentario: "",
    });
  };

  const crearReporte = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    setMensaje("");

    if (!miNodo) {
      setMensaje("❌ No tienes nodo asignado, no puedes reportar.");
      return;
    }

    if (!form.estado?.trim()) {
      setMensaje("❌ El estado es obligatorio.");
      return;
    }

    if (estadoNoVerde && !(form.comentario || "").trim()) {
      setMensaje("❌ Si el reporte no es verde, añade un comentario breve.");
      return;
    }

    setIsSubmitting(true);

    // ✅ Ya NO enviamos nodo_id (lo resuelve el backend por usuario)
    const payload = {
      estado: form.estado.trim(),
      comentario: (form.comentario || "").trim() || null,
    };

    try {
      const res = await fetch(`${Global.url}reportes`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...authHeaders(),
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        setMensaje(`❌ ${data?.message || "Error al crear el reporte."}`);
        return;
      }

      setMensaje("✅ Reporte registrado correctamente.");
      resetForm();

      if (typeof onCreated === "function") {
        onCreated(data?.data || data);
      }

      setTimeout(() => setMensaje(""), 2000);
    } catch (err) {
      console.error("Error al crear reporte:", err);
      setMensaje("❌ Error de conexión con el servidor.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const nombreUsuarioAsignado = useMemo(() => {
    if (!miNodo) return "";
    if (!miNodo.usuario_id) return "Sin asignar";
    return `${miNodo.nombre || ""} ${miNodo.apellidos || ""}`.trim() || "Sin asignar";
  }, [miNodo]);

  return (
    <div className="form-crear-usuario" style={{ maxWidth: 560 }}>
      <h2>Nuevo reporte</h2>

      {mensaje && (
        <p className={`mensaje ${mensaje.includes("❌") ? "rojo" : mensaje.includes("⚠️") ? "amarillo" : "verde"}`}>
          {mensaje}
        </p>
      )}

      {/* Info: nodo asignado */}
      <div className="form-group">
        <label>Nodo asignado:</label>
        {loadingNodo ? (
          <p className="muted">Cargando...</p>
        ) : miNodo ? (
          <div className="campo-readonly" style={{ padding: "10px 12px", border: "1px solid rgba(0,0,0,.12)", borderRadius: 8 }}>
            <div style={{ fontWeight: 700 }}>{miNodo.titulo}</div>
            <div className="muted" style={{ fontSize: 13 }}>
              {nombreUsuarioAsignado}{miNodo.etiqueta ? ` · ${miNodo.etiqueta}` : ""}
            </div>
          </div>
        ) : (
          <div className="campo-readonly" style={{ padding: "10px 12px", border: "1px solid rgba(0,0,0,.12)", borderRadius: 8 }}>
            <div style={{ fontWeight: 700 }}>Sin nodo asignado</div>
            <div className="muted" style={{ fontSize: 13 }}>
              No puedes enviar reportes hasta que te asignen un nodo.
            </div>
          </div>
        )}
      </div>

      <form onSubmit={crearReporte} className="formulario-usuario">
        {/* Estado / color */}
        <div className="form-group">
          <label>Estado:</label>
          <select
            value={form.estado}
            onChange={(e) => setForm((p) => ({ ...p, estado: e.target.value }))}
            required
            disabled={!miNodo}
          >
            <option value="verde">🟢 Verde (OK)</option>
            <option value="amarillo">🟡 Amarillo (Atención)</option>
            <option value="rojo">🔴 Rojo (Problema)</option>
          </select>
          <small className="muted">
            El panel mostrará el último reporte. Si caduca, tu hueco quedará en blanco.
          </small>
        </div>

        {/* Comentario */}
        <div className="form-group">
          <label>Comentario {estadoNoVerde ? "*" : "(opcional)"}:</label>
          <textarea
            value={form.comentario}
            onChange={(e) => setForm((p) => ({ ...p, comentario: e.target.value }))}
            placeholder={
              estadoNoVerde
                ? "Describe brevemente el problema (obligatorio si no es verde)"
                : "Opcional: nota rápida"
            }
            rows={3}
            disabled={!miNodo}
          />
          {estadoNoVerde && !(form.comentario || "").trim() && (
            <small className="rojo">Obligatorio si el estado no es verde.</small>
          )}
        </div>

        <button
          className="btn-login"
          type="submit"
          disabled={isSubmitting || !puedeEnviar}
          title={!miNodo ? "No tienes nodo asignado" : !puedeEnviar ? "Completa los campos requeridos" : undefined}
        >
          {isSubmitting ? "Enviando..." : "Registrar reporte"}
        </button>
      </form>
    </div>
  );
};
